import { t, Trans } from "@lingui/macro";
import { 
  Check, 
  Palette, 
  ArrowCounterClockwise, 
  Swatches, 
  Faders, 
  Hash, 
  MagicWand, 
  CheckCircle 
} from "@phosphor-icons/react";
import { defaultResumeData } from "@stylingcv/schema";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, Tooltip } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { HexColorPicker } from "react-colorful";

import { colors } from "@/client/constants/colors";
import { useResumeStore } from "@/client/stores/resume";

export const ThemeSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const theme = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.theme || defaultResumeData.metadata.theme;
    } catch {
      return defaultResumeData.metadata.theme;
    }
  });

  const updateTheme = (path: string, value: unknown) => {
    try {
      setValue(`metadata.theme.${path}`, value);
    } catch {
      // Silent fail
    }
  };

  const onResetTheme = () => {
    try {
      setValue("metadata.theme", defaultResumeData.metadata.theme);
    } catch {}
  };

  const currentPrimary = theme.primary || defaultResumeData.metadata.theme.primary;
  const currentBackground = theme.background || defaultResumeData.metadata.theme.background;
  const currentText = theme.text || defaultResumeData.metadata.theme.text;

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-5">
        <div className="flex items-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <Palette size={20} className="text-gray-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#16344c]">{t`Theme`}</h3>
            <p className="text-[11px] text-gray-400">{t`Colors & palette`}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors">
                <i className="ph ph-wrench" /> {/* Just a placeholder, optional icon setting */}
                <span className="sr-only">Icon Settings</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className="flex flex-col gap-y-1.5 p-3 w-64 bg-white border border-gray-200 rounded-xl shadow-lg">
              <Input
                value={(theme as any).icon || ""}
                placeholder={t`Enter Phosphor Icon`}
                onChange={(event) => {
                  updateTheme("icon", event.target.value);
                }}
                className="bg-white border border-gray-200 text-[#16344c] placeholder:text-gray-400 focus-visible:ring-[#268599]/50"
              />
              <p className="text-xs text-gray-400">
                <Trans>
                  Visit{" "}
                  <a
                    href="https://phosphoricons.com/"
                    target="_blank"
                    className="text-[#268599] underline"
                    rel="noopener noreferrer nofollow"
                  >
                    <Trans>Phosphor Icons</Trans>
                  </a>{" "}
                  for a list of available icons
                </Trans>
              </p>
            </PopoverContent>
          </Popover>

          <Tooltip content={t`Reset Theme`}>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors" onClick={onResetTheme}>
              <ArrowCounterClockwise size={18} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">

      {/* Predefined Primary Colors Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500">
            <Swatches size={18} weight="duotone" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{t`Accent Colors`}</span>
          </div>
        </div>

        <div className="grid grid-cols-6 gap-3 bg-gray-50 p-4 justify-items-center rounded-xl border border-gray-200">
          {colors.map((color) => {
            const isSelected = currentPrimary === color;
            return (
              <button
                key={color}
                onClick={() => updateTheme("primary", color)}
                className={cn(
                  "size-8 rounded-full transition-all group flex items-center justify-center relative overflow-hidden shrink-0",
                  isSelected
                    ? "border-2 border-[#16344c] scale-110 shadow-sm"
                    : "border border-gray-200 hover:scale-110"
                )}
                style={{ backgroundColor: color }}
              >
                <div className="absolute inset-0 rounded-full border border-black/20 mix-blend-overlay"></div>
                <Check
                  size={14}
                  weight="bold"
                  className={cn(
                    "text-white drop-shadow-md transition-opacity",
                    isSelected ? "opacity-100" : "opacity-0"
                  )}
                />
              </button>
            );
          })}
        </div>
      </div>

      <div className="h-px bg-gray-100 w-full"></div>

      {/* Hex Color Pickers */}
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-500">
            <Faders size={18} weight="duotone" />
            <span className="text-xs font-semibold uppercase tracking-wider">{t`Custom Hex Codes`}</span>
          </div>
        </div>

        {/* Primary Target */}
        <div className="space-y-1.5 focus-within:text-[#268599] transition-colors group">
          <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 group-focus-within:text-[#268599]">
            {t`Primary Color`}
          </label>
          <div className="relative flex items-center">
            <Popover>
              <PopoverTrigger asChild>
                <div className="absolute inset-y-0 left-3 flex items-center cursor-pointer z-10">
                  <button
                    className="w-5 h-5 rounded-full ring-2 ring-gray-200 ring-offset-2 ring-offset-white shadow-inner transition-transform hover:scale-110"
                    style={{ backgroundColor: currentPrimary }}
                  ></button>
                </div>
              </PopoverTrigger>
              <PopoverContent className="rounded-xl border border-gray-200 bg-white shadow-2xl p-0 overflow-hidden w-auto">
                <HexColorPicker
                  color={currentPrimary}
                  onChange={(color) => updateTheme("primary", color)}
                />
              </PopoverContent>
            </Popover>

            <Hash size={14} weight="bold" className="absolute left-10 text-gray-300 z-10" />
            <Input
              type="text"
              value={currentPrimary}
              onChange={(e) => updateTheme("primary", e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl py-6 pl-14 pr-4 font-mono text-sm text-[#16344c] focus-visible:outline-none focus-visible:border-[#268599] focus-visible:ring-1 focus-visible:ring-[#268599]/50 transition-all uppercase placeholder:text-gray-300"
            />
          </div>
        </div>

        {/* Background Target */}
        <div className="space-y-1.5 focus-within:text-blue-400 transition-colors group">
          <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 group-focus-within:text-blue-400">
            {t`Background Color`}
          </label>
          <div className="relative flex items-center">
            <Popover>
              <PopoverTrigger asChild>
                <div className="absolute inset-y-0 left-3 flex items-center cursor-pointer z-10">
                  <button
                    className="w-5 h-5 rounded-full ring-2 ring-gray-200 ring-offset-2 ring-offset-white shadow-inner transition-transform hover:scale-110 border border-gray-200"
                    style={{ backgroundColor: currentBackground }}
                  ></button>
                </div>
              </PopoverTrigger>
              <PopoverContent className="rounded-xl border border-gray-200 bg-white shadow-2xl p-0 overflow-hidden w-auto">
                <HexColorPicker
                  color={currentBackground}
                  onChange={(color) => updateTheme("background", color)}
                />
              </PopoverContent>
            </Popover>

            <Hash size={14} weight="bold" className="absolute left-10 text-gray-300 z-10" />
            <Input
              type="text"
              value={currentBackground}
              onChange={(e) => updateTheme("background", e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl py-6 pl-14 pr-4 font-mono text-sm text-[#16344c] focus-visible:outline-none focus-visible:border-blue-400 focus-visible:ring-1 focus-visible:ring-blue-400/50 transition-all uppercase placeholder:text-gray-300"
            />
          </div>
        </div>

        {/* Text Target */}
        <div className="space-y-1.5 focus-within:text-purple-400 transition-colors group">
          <label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 group-focus-within:text-purple-400">
            {t`Text Color`}
          </label>
          <div className="relative flex items-center">
            <Popover>
              <PopoverTrigger asChild>
                <div className="absolute inset-y-0 left-3 flex items-center cursor-pointer z-10">
                  <button
                    className="w-5 h-5 rounded-full ring-2 ring-gray-200 ring-offset-2 ring-offset-white shadow-inner transition-transform hover:scale-110 border border-gray-200"
                    style={{ backgroundColor: currentText }}
                  ></button>
                </div>
              </PopoverTrigger>
              <PopoverContent className="rounded-xl border border-gray-200 bg-white shadow-2xl p-0 overflow-hidden w-auto">
                <HexColorPicker
                  color={currentText}
                  onChange={(color) => updateTheme("text", color)}
                />
              </PopoverContent>
            </Popover>

            <Hash size={14} weight="bold" className="absolute left-10 text-gray-300 z-10" />
            <Input
              type="text"
              value={currentText}
              onChange={(e) => updateTheme("text", e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-xl py-6 pl-14 pr-4 font-mono text-sm text-[#16344c] focus-visible:outline-none focus-visible:border-purple-400 focus-visible:ring-1 focus-visible:ring-purple-400/50 transition-all uppercase placeholder:text-gray-300"
            />
          </div>
        </div>
      </div>

      {/* Mini Live Preview */}
      <div 
        className="mt-8 p-5 rounded-xl border relative overflow-hidden transition-colors duration-500 shadow-xl"
        style={{ backgroundColor: currentBackground, borderColor: `${currentPrimary}40` }}
      >
        <div className="flex items-center gap-2 mb-4" style={{ color: currentPrimary }}>
          <MagicWand size={16} />
          <span className="text-[10px] font-bold uppercase tracking-wider">{t`Preview Contrast`}</span>
        </div>
        
        <h3 className="font-bold text-xl mb-1 leading-tight" style={{ color: currentPrimary }}>Alex Smith</h3>
        <p className="text-xs font-semibold mb-4 uppercase tracking-wider opacity-80" style={{ color: currentText }}>{t`Senior Software Engineer`}</p>
        
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 text-xs" style={{ color: currentText }}>
            <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0" style={{ color: currentPrimary }} />
            <span className="opacity-90 leading-relaxed">{t`Created the live theme preview displaying contrast`}</span>
          </div>
        </div>
      </div>

      </div>

    </div>
  );
};
